"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { ChevronDown, BarChart3, BookOpen, Menu, Scale, X } from "lucide-react";
import { Logo } from "./logo";
import { WhatsAppFloat, WhatsAppIcon } from "./whatsapp-float";

const NAV = [
  { href: "/doc", label: "Pendahuluan" },
  { href: "/doc/registrasi", label: "Registrasi" },
  {
    href: "/doc/vpn-remote",
    label: "VPN Remote",
    children: [
      { href: "/doc/vpn-remote", label: "Order & kelola akun" },
      { href: "/doc/vpn-remote/konfigurasi", label: "Konfigurasi MikroTik" },
    ],
  },
  {
    href: "/doc/mikhmon",
    label: "Mikhmon Online",
    children: [
      { href: "/doc/mikhmon", label: "Order & panel" },
      { href: "/doc/mikhmon/konfigurasi", label: "Koneksi API router" },
    ],
  },
  { href: "/doc/ddns", label: "DDNS" },
];

function isActive(pathname: string, href: string) {
  if (href === "/doc") return pathname === "/doc";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function DocsShell({
  children,
  contactHref,
}: {
  children: React.ReactNode;
  contactHref: string;
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const sidebar = (
    <div className="flex h-full min-h-[calc(100vh-4.5rem)] flex-col">
      <nav className="flex-1 space-y-1 text-sm">
        {NAV.map((item) => {
          const active = isActive(pathname, item.href);
          return (
            <div key={item.href}>
              {item.children ? (
                <details open={active} className="group">
                  <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg px-3 py-2 text-slate-300 hover:bg-white/5 hover:text-white [&::-webkit-details-marker]:hidden">
                    <span className={active ? "font-medium text-teal-300" : ""}>{item.label}</span>
                    <ChevronDown className="h-4 w-4 transition group-open:rotate-180" />
                  </summary>
                  <div className="ml-3 space-y-1 border-l border-white/10 py-1 pl-3">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        onClick={() => setOpen(false)}
                        className={`block rounded-lg px-2 py-1.5 ${
                          pathname === child.href ? "text-teal-300" : "text-slate-400 hover:text-white"
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={`block rounded-lg px-3 py-2 ${
                    pathname === item.href ? "bg-white/5 font-medium text-teal-300" : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          );
        })}
      </nav>
      <div className="mt-6 space-y-1 border-t border-white/10 pt-4 text-sm">
        <a
          href={contactHref}
          target={contactHref.startsWith("http") ? "_blank" : undefined}
          rel={contactHref.startsWith("http") ? "noreferrer" : undefined}
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
          Hubungi Kami
        </a>
        <Link
          href="/doc"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <BookOpen className="h-4 w-4" />
          Panduan
        </Link>
        <Link
          href="/status"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <BarChart3 className="h-4 w-4" />
          Server Status
        </Link>
        <Link
          href="/tos"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-slate-400 hover:bg-white/5 hover:text-white"
        >
          <Scale className="h-4 w-4" />
          Syarat &amp; Ketentuan
        </Link>
      </div>
    </div>
  );

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-300">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button type="button" className="md:hidden text-white" onClick={() => setOpen(true)} aria-label="Buka menu dokumen">
              <Menu className="h-5 w-5" />
            </button>
            <Link href="/" className="text-white">
              <Logo />
            </Link>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link href="/" className="hidden text-slate-300 hover:text-white sm:inline">
              Beranda
            </Link>
            <Link href="/login" className="rounded-full bg-teal-500 px-4 py-2 font-semibold text-slate-950 hover:bg-teal-400">
              Masuk
            </Link>
          </div>
        </div>
      </header>

      {open ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setOpen(false)} />
          <aside className="relative h-full w-72 bg-slate-950 p-4">
            <div className="mb-4 flex justify-end">
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            {sidebar}
          </aside>
        </div>
      ) : null}

      <div className="mx-auto flex max-w-7xl">
        <aside className="hidden w-64 shrink-0 border-r border-white/10 bg-slate-950 px-4 py-8 md:block">{sidebar}</aside>
        <div className="min-w-0 flex-1 px-4 py-10 sm:px-8 lg:px-12">{children}</div>
      </div>
      <WhatsAppFloat href={contactHref} />
    </div>
  );
}

export function DocFigure({
  src,
  alt,
  caption,
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return (
    <figure className="my-8 overflow-hidden rounded-2xl border border-white/10 bg-slate-900">
      <img src={src} alt={alt} className="w-full" />
      {caption ? <figcaption className="px-4 py-3 text-sm text-slate-400">{caption}</figcaption> : null}
    </figure>
  );
}

export function DocPager({
  prev,
  next,
}: {
  prev?: { href: string; label: string };
  next?: { href: string; label: string };
}) {
  return (
    <div className="mt-14 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-6 text-sm">
      {prev ? (
        <Link href={prev.href} className="text-slate-400 hover:text-teal-300">
          ← {prev.label}
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link href={next.href} className="text-teal-300 hover:text-teal-200">
          {next.label} →
        </Link>
      ) : null}
    </div>
  );
}

export function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-500 text-xs font-bold text-slate-950">
        {n}
      </span>
      <div className="text-slate-300">{children}</div>
    </li>
  );
}
